import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { UserProfile } from '../../../../shared/types';
import { BaseModal } from '../common/BaseModal';
import { FormInputField } from '../form/FormInputField';
import { FormTextareaField } from '../form/FormTextareaField';
import { FormSelectField } from '../form/FormSelectField';
import { ModalButtonGroup } from '../common/ModalButtonGroup';
import { messageService, type Message, type MessagePriority, type MessageType } from '../../services/message.service';
import {
  getAllUsers,
  getCompanyAdministrators,
  getCompanyUsers,
  getManagerStaff,
} from '../../services/user.service';
import { ErrorMessage } from '../ErrorMessage';

interface ComposeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sender: UserProfile;
  onSent?: () => void;
  replyTo?: Message | null;
}

const MESSAGE_TYPES: { value: MessageType; label: string }[] = [
  { value: 'notification', label: 'Notification' },
  { value: 'alert', label: 'Alert' },
  { value: 'system', label: 'System' },
  { value: 'hr', label: 'HR' },
  { value: 'meeting', label: 'Meeting' },
];

const PRIORITY_OPTIONS: { value: MessagePriority; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
];

export function ComposeMessageModal({
  isOpen,
  onClose,
  sender,
  onSent,
  replyTo,
}: ComposeMessageModalProps) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [type, setType] = useState<MessageType>('notification');
  const [priority, setPriority] = useState<MessagePriority>('normal');
  const [isImportant, setIsImportant] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<{ value: string; label: string }[]>([]);
  const [recipientError, setRecipientError] = useState('');
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientDirectory, setRecipientDirectory] = useState<Record<string, { id: string; name: string; email?: string; department?: string }>>({});
  const selectedMessageType = MESSAGE_TYPES.find((option) => option.value === type);

  useEffect(() => {
    if (replyTo) {
      setTo(replyTo.fromUser?.id || replyTo.from);
      const replySubject = replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject}`;
      setSubject(replySubject);
      setType(replyTo.type);
      setPriority('normal');
      setIsImportant(false);
    }
  }, [replyTo, isOpen]);

  const resetForm = () => {
    setTo('');
    setSubject('');
    setBody('');
    setType('notification');
    setPriority('normal');
    setIsImportant(false);
    setError('');
  };

  const loadRecipients = useCallback(async () => {
    if (!isOpen) return;
    setRecipientsLoading(true);
    setRecipientError('');
    try {
      let users: UserProfile[] = [];

      if (sender.userType === 'super_admin') {
        users = await getAllUsers();
      } else if (sender.userType === 'company' && sender.companyId) {
        const [companyUsers, admins] = await Promise.all([
          getCompanyUsers(sender.companyId),
          getCompanyAdministrators(sender.companyId),
        ]);
        users = [...companyUsers, ...admins];
      } else if (sender.userType === 'manager') {
        const [staff, admins] = await Promise.all([
          getManagerStaff(sender.uid),
          sender.companyId ? getCompanyAdministrators(sender.companyId) : Promise.resolve([]),
        ]);
        users = [...staff, ...admins];
      }

      const unique = new Map<string, UserProfile>();
      users.forEach((user) => {
        if (user.uid && user.uid !== sender.uid) {
          unique.set(user.uid, user);
        }
      });

      const describeRole = (userType?: UserProfile['userType']) => {
        switch (userType) {
          case 'company':
            return 'Company Admin';
          case 'manager':
            return 'Manager';
          case 'staff':
            return 'Staff';
          case 'super_admin':
            return 'Super Admin';
          default:
            return 'User';
        }
      };

      const formatName = (user: UserProfile) => {
        if (user.firstName || user.lastName) {
          return `${user.firstName || ''} ${user.lastName || ''}`.trim();
        }
        return user.email || 'Unnamed User';
      };

      const directory: Record<string, { id: string; name: string; email?: string; department?: string }> = {};
      Array.from(unique.values()).forEach((user) => {
        directory[user.uid] = {
          id: user.uid,
          name: formatName(user),
          email: user.email,
          department: describeRole(user.userType),
        };
      });

      const options = Array.from(unique.values())
        .sort((a, b) => formatName(a).localeCompare(formatName(b)))
        .map((user) => ({
          value: user.uid,
          label: `${formatName(user)} — ${describeRole(user.userType)}${user.email ? ` (${user.email})` : ''}`,
        }));

      setRecipientOptions(options);
      setRecipientDirectory(directory);

      if (!replyTo && options.length > 0) {
        setTo((current) => (current ? current : options[0].value));
      }
    } catch (err: unknown) {
      console.error('Error loading recipients:', err);
      setRecipientError('Failed to load recipients. Please try again.');
      setRecipientOptions([]);
    } finally {
      setRecipientsLoading(false);
    }
  }, [sender, isOpen, replyTo]);

  useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!to.trim()) {
      setError('Please choose a recipient.');
      return;
    }

    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }

    if (!body.trim()) {
      setError('Message body is required.');
      return;
    }

    const fromName =
      sender.firstName && sender.lastName ? `${sender.firstName} ${sender.lastName}` : sender.email || 'Unknown Sender';

    setLoading(true);
    try {
      const selectedRecipient = recipientDirectory[to.trim()];

      const messageInput = {
        to: to.trim(),
        from: sender.uid,
        fromName,
        subject: subject.trim(),
        body: body.trim(),
        type,
        priority,
        isImportant,
        fromUser: {
          id: sender.uid,
          name: fromName,
          ...(sender.avatar ? { avatar: sender.avatar } : {}),
          ...(sender.companyId ? { department: 'Company' } : {}),
        },
        ...(selectedRecipient
          ? {
              toUser: {
                id: selectedRecipient.id,
                name: selectedRecipient.name,
                email: selectedRecipient.email,
                department: selectedRecipient.department,
              },
            }
          : {}),
        ...(replyTo
          ? {
              conversationId: replyTo.conversationId,
              replyTo: replyTo.id,
            }
          : {}),
      };

      await messageService.sendMessage(messageInput);
      onSent?.();
      resetForm();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title={replyTo ? 'Reply to Message' : 'Compose Message'} size="medium">
      <form onSubmit={handleSubmit}>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <ErrorMessage message={error} onDismiss={() => setError('')} />
          </div>
        )}

        <FormSelectField
          label="Recipient"
          value={to}
          onChange={setTo}
          options={recipientOptions}
          placeholder={recipientsLoading ? 'Loading recipients...' : 'Select recipient'}
          required
          disabled={!!replyTo || recipientsLoading}
          error={recipientError}
          helperText={
            !recipientError && !recipientsLoading && recipientOptions.length === 0
              ? 'No available recipients.'
              : undefined
          }
        />

        <FormInputField
          label="Subject"
          value={subject}
          onChange={setSubject}
          placeholder="Subject"
          required
        />

        {replyTo ? (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#374151' }}>
              Message Type
            </label>
            <div
              style={{
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #e5e7eb',
                backgroundColor: '#f9fafb',
                color: '#111827',
              }}
            >
              {selectedMessageType?.label ?? type}
            </div>
          </div>
        ) : (
          <FormSelectField
            label="Message Type"
            value={type}
            onChange={(value) => setType(value)}
            options={MESSAGE_TYPES}
            required
          />
        )}

        <FormSelectField
          label="Priority"
          value={priority}
          onChange={(value) => setPriority(value)}
          options={PRIORITY_OPTIONS}
          required
        />

        <FormTextareaField
          label="Message"
          value={body}
          onChange={setBody}
          placeholder="Write your message..."
          rows={5}
          required
        />

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <input
            type="checkbox"
            checked={isImportant}
            onChange={(e) => setIsImportant(e.target.checked)}
            style={{ width: '16px', height: '16px' }}
          />
          <span style={{ fontSize: '14px', color: '#374151' }}>Mark as important</span>
        </label>

        <ModalButtonGroup
          onCancel={handleClose}
          submitLabel={replyTo ? 'Send Reply' : 'Send Message'}
          isLoading={loading}
          loadingLabel="Sending..."
        />
      </form>
    </BaseModal>
  );
}
